/**
 * CadenceCell — display of the four post-reply engagement metrics inside
 * a single MasterInbox table cell. Rendered only for rows where `metrics`
 * is non-null (human + positive replies). Other rows show a single em-dash.
 *
 * Layout (4 rows, full label + value):
 *   Speed to Lead    2h 14m
 *   Avg Follow-Ups   4
 *   Between FUs      3d
 *   Reply → Book     6h 48m
 *
 * Em-dash renders where a metric is null (no SDR follow-up captured,
 * < 2 follow-ups for between-gap, no Meeting Booked label change yet).
 */
import React from 'react'
import {
  formatCadenceDuration,
  type ReplyCadenceMetrics,
} from '@/hooks/useReplyCadenceBatch'

interface Props {
  metrics: ReplyCadenceMetrics | null | undefined
}

export function CadenceCell({ metrics }: Props) {
  if (!metrics) {
    return <span className="text-sm text-muted-foreground">—</span>
  }

  const stl = formatCadenceDuration(metrics.speed_to_lead_seconds)
  const fuCount = metrics.followups_count > 0 ? `${metrics.followups_count}` : '—'
  const between =
    metrics.followups_count >= 2
      ? formatCadenceDuration(metrics.between_fus_seconds)
      : '—'
  const book = formatCadenceDuration(metrics.reply_to_book_seconds)

  return (
    <div className="flex flex-col gap-1 text-[12px] leading-snug">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-muted-foreground">Speed to Lead</span>
        <span className="font-mono font-medium text-foreground">{stl}</span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-muted-foreground">Avg Follow-Ups</span>
        <span className="font-mono font-medium text-foreground">{fuCount}</span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-muted-foreground">Between FUs</span>
        <span className="font-mono font-medium text-foreground">{between}</span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-muted-foreground">Reply → Book</span>
        <span className="font-mono font-medium text-foreground">{book}</span>
      </div>
    </div>
  )
}
